import { useTranslation } from 'react-i18next';
import { FolderSimple } from '@phosphor-icons/react';
import type { UserDocumentDto } from '@monorepo/shared';

interface UserDocumentsTableProps {
  documents: UserDocumentDto[];
  isLoading: boolean;
  onOpenDocument: (nodeId: string) => void;
}

export const UserDocumentsTable = ({
  documents,
  isLoading,
  onOpenDocument,
}: UserDocumentsTableProps) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <div className="h-6 w-40 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="p-4 space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="flex items-center gap-2 p-4 border-b border-gray-200">
        <FolderSimple size={20} weight="regular" className="text-primary" />
        <h2 className="text-lg font-semibold text-gray-900">
          {t('dashboard.user_documents.title')}
        </h2>
      </div>

      {documents.length === 0 ? (
        <div className="p-8 text-center text-gray-500">{t('dashboard.user_documents.empty')}</div>
      ) : (
        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('dashboard.user_documents.columns.name')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('dashboard.user_documents.columns.folder')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('dashboard.user_documents.columns.theme')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('dashboard.user_documents.columns.action')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {documents.map((doc) => (
                <tr key={doc.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-gray-900">{doc.name}</span>
                  </td>
                  <td className="px-4 py-3">
                    {doc.folderName && (
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <FolderSimple size={16} weight="regular" />
                        {doc.folderName}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {doc.themeName && (
                      <span className="px-2 py-1 text-xs font-medium rounded-md bg-gray-100 text-gray-700">
                        {doc.themeName}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => onOpenDocument(doc.nodeId)}
                      className="px-4 py-1.5 text-sm font-medium text-white bg-primary rounded-md hover:bg-opacity-90 transition"
                    >
                      {t('dashboard.user_documents.actions.open')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
